import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const MAP: Record<string, string> = {
  s: "/settings",
  e: "/excerpts",
  r: "/readwise-highlights",
  h: "/excerpts",
};

const isTypingTarget = (el: Element | null): boolean => {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
};

export const useGlobalShortcuts = () => {
  const navigate = useNavigate();
  const leaderActiveRef = useRef(false);
  const leaderTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const clearLeader = () => {
      leaderActiveRef.current = false;
      if (leaderTimerRef.current !== null) {
        window.clearTimeout(leaderTimerRef.current);
        leaderTimerRef.current = null;
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const target = (e.target as Element | null) ?? document.activeElement;
      if (isTypingTarget(target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (!leaderActiveRef.current && (e.key === "n" || e.key === "N")) {
        e.preventDefault();
        e.stopPropagation();
        navigate("/new-excerpt");
        return;
      }

      if (!leaderActiveRef.current && e.key === "/") {
        const input = document.querySelector<HTMLInputElement>("[data-search-input]");
        if (input) {
          e.preventDefault();
          e.stopPropagation();
          input.focus();
          input.select?.();
        }
        return;
      }

      if (leaderActiveRef.current) {
        clearLeader();
        const dest = MAP[e.key.toLowerCase()];
        if (dest) {
          e.preventDefault();
          e.stopPropagation();
          navigate(dest);
        }
        return;
      }

      if (e.key === "g" || e.key === "G") {
        leaderActiveRef.current = true;
        leaderTimerRef.current = window.setTimeout(() => {
          leaderActiveRef.current = false;
          leaderTimerRef.current = null;
        }, 2000);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      clearLeader();
    };
  }, [navigate]);
};
